import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Plus, Search, Package, Edit, Trash2, ToggleLeft, ToggleRight } from 'lucide-react';
import { useNavigation } from '../context/NavigationContext';
import { useVendor } from '../context/VendorContext';
import { VendorProduct } from '../types';
import { Screen } from '../types';
import { Header } from '../components/ui/Header';
import { Button } from '../components/ui/Button';
export function VendorProductsScreen() {
  const {
    navigate
  } = useNavigation();
  const {
    products,
    updateProduct,
    deleteProduct
  } = useVendor();
  const [activeTab, setActiveTab] = useState<'all' | 'active' | 'inactive' | 'outOfStock'>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const tabs = [{
    key: 'all' as const,
    label: 'All Products'
  }, {
    key: 'active' as const,
    label: 'Active'
  }, {
    key: 'inactive' as const,
    label: 'Inactive'
  }, {
    key: 'outOfStock' as const,
    label: 'Out of Stock'
  }];
  const filteredProducts = products.filter(product => {
    const matchesSearch = product.name.toLowerCase().includes(searchQuery.toLowerCase());
    if (activeTab === 'all') return matchesSearch;
    if (activeTab === 'active') return matchesSearch && product.isActive && product.stock > 0;
    if (activeTab === 'inactive') return matchesSearch && !product.isActive;
    if (activeTab === 'outOfStock') return matchesSearch && product.stock === 0;
    return matchesSearch;
  });
  const handleToggleActive = (productId: string, currentStatus: boolean) => {
    updateProduct(productId, {
      isActive: !currentStatus
    });
  };
  const handleDeleteProduct = (productId: string) => {
    if (confirm('Are you sure you want to delete this product?')) {
      deleteProduct(productId);
    }
  };
  return <motion.div initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} className="min-h-screen bg-gray-50 pb-24" data-id="element-2178">
      <Header title="Products" showBack={false} data-id="element-2179" />

      {/* Search & Add Button */}
      <div className="bg-white p-4 border-b border-gray-100 sticky top-[53px] z-50" data-id="element-2180">
        <div className="flex gap-2 mb-3" data-id="element-2181">
          <div className="flex-1 relative" data-id="element-2182">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" data-id="element-2183" />
            <input type="text" placeholder="Search products..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="w-full pl-10 pr-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary" data-id="element-2184" />
          </div>
          <button onClick={() => navigate(Screen.VENDOR_ADD_PRODUCT)} className="w-11 h-11 bg-primary text-white rounded-xl flex items-center justify-center shadow-sm active:scale-95 transition-transform cursor-pointer touch-manipulation" data-id="element-2185">
            <Plus className="w-5 h-5" data-id="element-2186" />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 overflow-x-auto" data-id="element-2187">
          {tabs.map(tab => <button key={tab.key} onClick={() => setActiveTab(tab.key)} className={`px-4 py-2 text-sm font-medium rounded-lg whitespace-nowrap transition-colors ${activeTab === tab.key ? 'bg-primary text-white' : 'bg-gray-100 text-gray-600'}`} data-id="element-2188">
              {tab.label}
              <span className="ml-1.5 text-xs" data-id="element-2189">
                (
                {tab.key === 'all' ? products.length : tab.key === 'active' ? products.filter(p => p.isActive && p.stock > 0).length : tab.key === 'inactive' ? products.filter(p => !p.isActive).length : products.filter(p => p.stock === 0).length}
                )
              </span>
            </button>)}
        </div>
      </div>

      {/* Products List */}
      <div className="p-4 space-y-3" data-id="element-2190">
        {filteredProducts.length > 0 ? filteredProducts.map((product, idx) => <motion.div key={product.id} initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: idx * 0.05
      }} className="bg-white rounded-2xl shadow-sm border border-gray-50 overflow-hidden" data-id="element-2191">
              <div className="flex gap-3 p-4" data-id="element-2192">
                {/* Product Image */}
                <div className="relative" data-id="element-2193">
                  <img src={product.images[0]} alt={product.name} className="w-24 h-24 rounded-xl object-cover" data-id="element-2194" />
                  {product.stock === 0 && <div className="absolute inset-0 bg-black/60 rounded-xl flex items-center justify-center" data-id="element-2195">
                      <span className="text-white text-xs font-bold" data-id="element-2196">
                        Out of Stock
                      </span>
                    </div>}
                </div>

                {/* Product Info */}
                <div className="flex-1 min-w-0" data-id="element-2197">
                  <div className="flex items-start justify-between gap-2 mb-1" data-id="element-2198">
                    <h3 className="font-medium text-gray-900 truncate" data-id="element-2199">
                      {product.name}
                    </h3>
                    <button onClick={() => handleToggleActive(product.id, product.isActive)} className="flex-shrink-0" data-id="element-2200">
                      {product.isActive ? <ToggleRight className="w-10 h-10 text-primary" data-id="element-2201" /> : <ToggleLeft className="w-10 h-10 text-gray-300" data-id="element-2202" />}
                    </button>
                  </div>

                  <p className="text-xs text-gray-500 mb-2" data-id="element-2203">
                    {product.category}
                  </p>

                  <div className="flex items-center gap-2 mb-2" data-id="element-2204">
                    <span className="text-lg font-bold text-primary" data-id="element-2205">
                      ₦
                      {(product.discountPrice || product.price).toLocaleString()}
                    </span>
                    {product.discountPrice && <span className="text-sm text-gray-400 line-through" data-id="element-2206">
                        ₦{product.price.toLocaleString()}
                      </span>}
                  </div>

                  <div className="flex items-center gap-3" data-id="element-2207">
                    <span className={`text-xs px-2 py-1 rounded-full ${product.stock > 10 ? 'bg-success/10 text-success' : product.stock > 0 ? 'bg-warning/10 text-warning' : 'bg-error/10 text-error'}`} data-id="element-2208">
                      Stock: {product.stock}
                    </span>
                    <span className={`text-xs px-2 py-1 rounded-full ${product.isActive ? 'bg-success/10 text-success' : 'bg-gray-100 text-gray-600'}`} data-id="element-2209">
                      {product.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="px-4 pb-4 flex gap-2" data-id="element-2210">
                <button onClick={() => navigate(Screen.VENDOR_EDIT_PRODUCT)} className="flex-1 flex items-center justify-center gap-2 py-2.5 bg-primary/5 text-primary rounded-xl font-medium text-sm active:scale-95 transition-transform" data-id="element-2211">
                  <Edit className="w-4 h-4" data-id="element-2212" />
                  Edit
                </button>
                <button onClick={() => handleDeleteProduct(product.id)} className="flex-1 flex items-center justify-center gap-2 py-2.5 bg-error/5 text-error rounded-xl font-medium text-sm active:scale-95 transition-transform" data-id="element-2213">
                  <Trash2 className="w-4 h-4" data-id="element-2214" />
                  Delete
                </button>
              </div>
            </motion.div>) : <div className="flex flex-col items-center justify-center py-12 text-center" data-id="element-2215">
            <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mb-4" data-id="element-2216">
              <Package className="w-10 h-10 text-gray-400" data-id="element-2217" />
            </div>
            <h3 className="text-lg font-bold text-gray-900 mb-1" data-id="element-2218">
              No Products Found
            </h3>
            <p className="text-sm text-gray-500 mb-4" data-id="element-2219">
              {searchQuery ? 'Try a different search term' : 'Start by adding your first product'}
            </p>
            <Button onClick={() => navigate(Screen.VENDOR_ADD_PRODUCT)} icon={<Plus className="w-4 h-4" data-id="element-2221" />} data-id="element-2220">
              Add Product
            </Button>
          </div>}
      </div>
    </motion.div>;
}